import { setAuth } from "@/modules/auth/states";
import requestConfig from "@/shared/config/request";
import { deleteCookie } from "@/shared/utils/cookie-helper";
import parsePromise from "@/shared/utils/parsePromise";
import axios, { AxiosRequestConfig, AxiosResponse } from "axios";

enum Methods {
  POST = "POST",
  PUT = "PUT",
  GET = "GET",
  DELETE = "DELETE",
  PATCH = "PATCH",
}

// Create axios client with credentials enabled by default
const axiosClient = axios.create({
  // baseURL: requestConfig.baseUrl,
  baseURL: "/api",
  headers: {
    Accept: "application/json",
    "Content-Type": "application/json",
  },
  withCredentials: true, // Enable cookies by default
});

interface CommonProps<D = any> {
  url: string;
  config?: AxiosRequestConfig<D>;
  useCookies?: boolean;
  isAuthRequired?: boolean;
}

// Track if we're currently refreshing the token to prevent multiple refresh attempts
let isRefreshingToken = false;
// Queue of requests that should be retried after token refresh
let requestsQueue: Array<() => void> = [];

// Helper to process the queue of pending requests
const processQueue = () => {
  requestsQueue.forEach((callback) => callback());
  requestsQueue = [];
};

// Token refresh function extracted for clarity
const refreshToken = async (): Promise<boolean> => {
  if (isRefreshingToken) {
    // Return a promise that resolves when the current refresh completes
    return new Promise((resolve) => {
      requestsQueue.push(() => resolve(true));
    });
  }

  isRefreshingToken = true;

  try {
    // Use axiosClient directly to avoid circular dependency with makeRequest
    await axiosClient.request({
      url: "/v0/auth/refresh-token",
      method: "POST",
      withCredentials: true, // Ensure cookies are sent
    });

    isRefreshingToken = false;
    processQueue();
    return true;
  } catch (error) {
    console.error("Token refresh failed");

    // Clear auth state on refresh failure
    deleteCookie("intermediate_session_token");
    setAuth({
      isAuthenticated: false,
      isAuthenticationInProgress: false,
      loggedInMember: null,
      loggedInOrganization: null
    });

    isRefreshingToken = false;
    processQueue();
    return false;
  }
};

export const makeRequest = async <T, D = any>({
  url,
  method,
  config,
  useCookies = true,
  isAuthRequired = true,
  retryCount = 0,
  _isRefreshAttempt = false, // Internal flag to prevent refresh loops
}: CommonProps<D> & {
  method: Methods;
  retryCount?: number;
    _isRefreshAttempt?: boolean;
}): Promise<T> => {
  if (retryCount >= 3) {
    throw new Error("Max retry attempts reached");
  }

  try {
    const response = await axiosClient.request<T>({
      url,
      method,
      // Special case for signed_url, otherwise default to useCookies value
      withCredentials: url.includes("signed_url") ? false : useCookies,
      ...config,
      // Don't manually set auth headers - rely on cookies
    });

    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      console.error(error?.message);
    }
    if (
      axios.isAxiosError(error) &&
      error.response?.status === 401 &&
      isAuthRequired &&
      !_isRefreshAttempt && // Prevent refresh loops
      retryCount < 2 // Limit refresh attempts
    ) {
      if (retryCount === 0) {
          // refresh token
          await parsePromise(makeRequest({
            url: "/v0/auth/refresh-token",
            method: Methods.POST,
            config: {},
            useCookies: true,
            isAuthRequired: true,
            retryCount: retryCount + 1,
          }));
        }        
      // delete the cookie if the token is expired as it can be a problem with the cookie
      if (retryCount >= 2) {
        deleteCookie("intermediate_session_token");
        setAuth({
          isAuthenticated: false,
          isAuthenticationInProgress: false,
        })
      }
      if (retryCount > 1 && retryCount < 3) {
        try {
          return makeRequest({
            url,
            method,
            config,
            useCookies,
            isAuthRequired,
            retryCount: retryCount + 1,
          });
        } catch (err) {
          console.error(error?.message)
          throw err;
        }
      }

    }

    // For all other errors or if token refresh failed
    if (axios.isAxiosError(error)) {
      console.error(`API Request failed: ${method} ${url}`, error?.message);

      // If we've had multiple 401 errors, clear auth state
      if (error.response?.status === 401) {
        if (retryCount >= 2) {
          deleteCookie("intermediate_session_token");
          setAuth({
            isAuthenticated: false,
            isAuthenticationInProgress: false,
            loggedInMember: null,
            loggedInOrganization: null
          }); 
        }
        
        // Return a consistent error object instead of throwing
        // This prevents the error from reaching Bugsnag
        return { error: 'Unauthorized', status: 401 } as T;
      }
    }

    throw error as Error;
  }
};

export const get = async <T>({
  url,
  config,
  useCookies = true,
  isAuthRequired = true,
}: CommonProps): Promise<T> =>
  makeRequest<T>({
    url,
    method: Methods.GET,
    config,
    useCookies,
    isAuthRequired,
  });

export const post = async <D, T>({
  url,
  data,
  config,
  useCookies = true,
  isAuthRequired = true,
}: CommonProps<D> & {
  data: D;
}): Promise<T> =>
  makeRequest<T>({
    url,
    method: Methods.POST,
    config: { data, ...config },
    useCookies,
    isAuthRequired,
  });

export const put = async <D, T>({
  url,
  data,
  config,
  useCookies = true,
  isAuthRequired = true,
}: CommonProps<D> & {
  data: D;
}): Promise<T> =>
  makeRequest<T>({
    url,
    method: Methods.PUT,
    config: { data, ...config },
    useCookies,
    isAuthRequired,
  });

export const del = async <T>({
  url,
  config,
  useCookies = true,
  isAuthRequired = true,
}: CommonProps): Promise<T> =>
  makeRequest<T>({
    url,
    method: Methods.DELETE,
    config: { ...config },
    useCookies,
    isAuthRequired,
  });

export const patch = async <T>({
  url,
  config,
  useCookies = true,
  isAuthRequired = true,
}: CommonProps): Promise<T> =>
  makeRequest<T>({
    url,
    method: Methods.PATCH,
    config,
    useCookies,
    isAuthRequired,
  });

export const request = {
  get,
  post,
  put,
  del,
  patch,
};
